import config from '@/config'

type Grecaptcha = {
  ready: (cb: () => void) => void
  execute: (siteKey: string, options: { action: string }) => Promise<string>
}

declare global {
  interface Window {
    grecaptcha?: Grecaptcha
  }
}

const SCRIPT_ID = 'recaptcha-script'
const BADGE_STYLE_ID = 'recaptcha-style'
let loadPromise: Promise<void> | null = null

function ensureBadgeStyle(): void {
  if (typeof document === 'undefined') return
  if (document.getElementById(BADGE_STYLE_ID)) return
  const style = document.createElement('style')
  style.id = BADGE_STYLE_ID
  style.textContent = '.grecaptcha-badge { visibility: visible !important; }'
  document.head.appendChild(style)
}

export function showRecaptchaBadge(): void {
  ensureBadgeStyle()
}

export function hideRecaptchaBadge(): void {
  if (typeof document === 'undefined') return
  const style = document.getElementById(BADGE_STYLE_ID)
  if (style) style.remove()
}

export function loadRecaptcha(): Promise<void> {
  if (loadPromise) return loadPromise

  const promise = new Promise<void>((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('reCAPTCHA unavailable'))
      return
    }

    if (!config.GOOGLE_RECAPTCHA_CLIENT_KEY) {
      reject(new Error('reCAPTCHA client key missing'))
      return
    }

    const waitForReady = () => {
      if (!window.grecaptcha?.ready) {
        reject(new Error('reCAPTCHA failed to initialize'))
        return
      }
      window.grecaptcha.ready(() => resolve(undefined))
    }

    if (window.grecaptcha?.ready) {
      waitForReady()
      return
    }

    if (typeof document === 'undefined') {
      reject(new Error('reCAPTCHA unavailable'))
      return
    }

    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null
    if (existing) {
      existing.addEventListener('load', waitForReady)
      existing.addEventListener('error', () => reject(new Error('reCAPTCHA failed to load')))
      return
    }

    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.src = `https://www.google.com/recaptcha/api.js?render=${config.GOOGLE_RECAPTCHA_CLIENT_KEY}`
    script.async = true
    script.defer = true
    script.onload = waitForReady
    script.onerror = () => reject(new Error('reCAPTCHA failed to load'))
    document.head.appendChild(script)
  }).catch((err) => {
    loadPromise = null
    throw err
  })

  loadPromise = promise
  return promise
}

export async function getRecaptchaToken(action: string): Promise<string> {
  await loadRecaptcha()
  if (!window.grecaptcha?.execute) {
    throw new Error('reCAPTCHA unavailable')
  }
  return window.grecaptcha.execute(config.GOOGLE_RECAPTCHA_CLIENT_KEY, { action })
}
